import _ from "lodash";
import { cache } from "lib/apolloClient";
import {
  getLearningCourseDetailsQuery,
  getLearningCourseGradingPeriodsQuery,
  getLearningCoursesWithoutPaginationQuery,
} from "./LearningCourseQueries";
import {
  learningCourseDetailsFragment,
  subjectFragment,
} from "./LearningCourseFragments";
import { getPlatformUserDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { getCurrentAcademicYear } from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getFilteredOrganizationAcademicYearsMemo } from "Platform/components/GlobalHeader/components/PlatformSwitcher/PlatformSwitcherUtils";

const EMPTY_ARRAY = [];

/**
 * Returns the academic years accessible to the current user — i.e. the same set surfaced by the
 * global Academic Year switcher (current + past + the immediately upcoming AY if planning is
 * allowed).
 **/
export const getCurrentUserAccessibleAcademicYears = ({
  isAdminPage = false,
  isAccountOrSchoolPortal = false,
} = {}) => {
  const userInfo = getUserInfo();
  const userDataFromCache = getPlatformUserDetailsFromCache({
    id: userInfo.id,
    type: userInfo.userEntityType,
  });
  const currentAcademicYear = getCurrentAcademicYear({
    organizationId: userInfo?.org_id,
  });
  return getFilteredOrganizationAcademicYearsMemo({
    academicYears: _.get(userDataFromCache, "academicYears", EMPTY_ARRAY),
    currentAcademicYear,
    isAdminPage,
    isAccountOrSchoolPortal,
  });
};

export const getCurrentUserAccessibleAcademicYearIds = params =>
  _.map(getCurrentUserAccessibleAcademicYears(params), "id");

export const getLearningCourseDetailsFromCache = variables => {
  let queryData = {};
  try {
    queryData = cache.readQuery({
      query: getLearningCourseDetailsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", []);
  else return {};
};

export const getLearningCourseFragmentFromCache = ({ id, variables }) => {
  let queryData = {};
  try {
    queryData = cache.readFragment({
      id: `LearningCourse:${id}`,
      fragment: learningCourseDetailsFragment,
      variables,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getLearningCourseGradingPeriodsFromCache = variables => {
  try {
    const queryData = cache.readQuery({
      query: getLearningCourseGradingPeriodsQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getLearningCoursesWithoutPaginationFromCache = variables => {
  try {
    const queryData = cache.readQuery({
      query: getLearningCoursesWithoutPaginationQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getSubjectDetailFromCache = ({ subjectId }) => {
  let fragmentData = {};
  try {
    fragmentData = cache.readFragment({
      id: `Subject:${subjectId}`,
      fragment: subjectFragment,
      fragmentName: "subjectItem",
    });
  } catch (e) {
    return {};
  }
  return fragmentData;
};
